/**
 * Woodland mansion (vanilla WoodlandMansionPieces), at a fraction of vanilla's
 * size: a two-storey dark oak house on a cobblestone plinth — dark oak log
 * frame every four blocks, dark oak plank walls, birch floors, rows of windows
 * — under a stepped dark oak roof. Inside, a carpeted hall runs front to back
 * with rooms off either side: a library, bedrooms, and a storeroom of chests.
 */

import { SEA_LEVEL } from '../../constants';
import { AIR, WOOL_IDS } from '../../blocks';
import { Biome } from '../biomes';
import type { TerrainGenerator } from '../terrain';
import { block, type Builder, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType } from './kit';

const COBBLE = block('Cobblestone');
const LOG = block('Dark Oak Log');
const WALL = block('Dark Oak Planks');
const FLOOR = block('Birch Planks');
const ROOF = block('Dark Oak Planks');
const GLASS = block('Glass');
const SHELF = block('Bookshelf');
const TORCH = block('Torch');

const W = 25, D = 21, STOREY = 5;

/** One storey's shell at `y` (floor) with its windows and the log frame. */
function storey(b: Builder, y: number): void {
  b.fill(0, y, 0, W - 1, y, D - 1, FLOOR);
  b.walls(0, y + 1, 0, W - 1, y + STOREY - 1, D - 1, WALL);
  for (let u = 0; u < W; u += 4) { b.fill(u, y, 0, u, y + STOREY - 1, 0, LOG); b.fill(u, y, D - 1, u, y + STOREY - 1, D - 1, LOG); }
  for (let v = 0; v < D; v += 4) { b.fill(0, y, v, 0, y + STOREY - 1, v, LOG); b.fill(W - 1, y, v, W - 1, y + STOREY - 1, v, LOG); }
  b.walls(0, y + STOREY, 0, W - 1, y + STOREY, D - 1, LOG);
  // two-high windows between the posts
  for (let u = 2; u < W - 1; u += 4) for (const v of [0, D - 1]) b.fill(u, y + 2, v, u, y + 3, v, GLASS);
  for (let v = 2; v < D - 1; v += 4) for (const u of [0, W - 1]) b.fill(u, y + 2, v, u, y + 3, v, GLASS);
}

function buildMansion(b: Builder): void {
  const y = b.y;
  b.fill(-1, y + 1, -1, W, y + STOREY * 2 + 10, D, AIR);
  b.foundations(0, 0, W - 1, D - 1, y, COBBLE, 10);
  b.fill(0, y, 0, W - 1, y, D - 1, COBBLE);
  storey(b, y);
  storey(b, y + STOREY);
  const mid = W >> 1;
  for (const fy of [y, y + STOREY]) {
    // the hall down the middle, carpeted red, with rooms either side
    b.fill(mid - 1, fy, 1, mid + 1, fy, D - 2, WOOL_IDS.red);
    for (const u of [mid - 2, mid + 2]) b.fill(u, fy + 1, 1, u, fy + STOREY - 1, D - 2, WALL);
    for (const v of [7, 14]) {
      b.fill(1, fy + 1, v, mid - 3, fy + STOREY - 1, v, WALL);
      b.fill(mid + 3, fy + 1, v, W - 2, fy + STOREY - 1, v, WALL);
    }
    // doors from the hall into each room
    for (const v of [4, 11, 17]) for (const u of [mid - 2, mid + 2]) b.fill(u, fy + 1, v, u, fy + 2, v, AIR);
    for (const v of [3, 10, 17]) { b.set(mid - 1, fy + 3, v, TORCH); b.set(mid + 1, fy + 3, v, TORCH); }
  }
  b.door('Spruce Door', mid, y + 1, 0, F.PV);
  b.set(mid - 1, y + 1, 0, AIR); b.set(mid + 1, y + 1, 0, AIR);
  // the stair to the upper floor: a stepped block run in the hall's back end
  for (let k = 0; k < STOREY; k++) b.set(mid, y + 1 + k, D - 2 - STOREY + k, WALL);
  b.fill(mid, y + STOREY, D - 2 - STOREY, mid, y + STOREY, D - 3, AIR);
  // ground floor: library (left front) and dining room (right front)
  for (let v = 1; v < 7; v++) b.fill(1, y + 1, v, 1, y + 3, v, SHELF);
  for (let u = 1; u < mid - 2; u++) b.fill(u, y + 1, 1, u, y + 3, 1, SHELF);
  b.fill(mid + 5, y + 1, 3, W - 4, y + 1, 4, block('Dark Oak Planks'));
  // upper floor: bedrooms and the storeroom
  for (const [u, v] of [[3, 2], [W - 4, 2], [3, 9], [W - 4, 9]]) b.bed(u, y + STOREY + 1, v, F.PV);
  for (let u = 2; u < mid - 3; u += 2) b.chest(u, y + STOREY + 1, D - 2, F.NV);
  for (let u = mid + 4; u < W - 2; u += 2) b.set(u, y + STOREY + 1, D - 2, SHELF);
  // the roof: steps in from every side
  const top = y + STOREY * 2 + 1;
  for (let i = 0; ; i++) {
    const u0 = -1 + i, u1 = W - i, v0 = -1 + i, v1 = D - i;
    if (u0 > u1 || v0 > v1) break;
    b.walls(u0, top + i, v0, u1, top + i, v1, ROOF);
    if (v1 - v0 <= 2) { b.fill(u0, top + i, v0, u1, top + i, v1, ROOF); break; }
  }
}

function mansion(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if ((c.biome !== Biome.DarkForest && c.biome !== Biome.PaleGarden) || c.height < SEA_LEVEL + 1) return null;
  return [centredPiece(x, z, int(rnd, 4), W, D, c.height, true, seedOf(rnd), buildMansion)];
}

export const WOODLAND_MANSION: StructureType = {
  name: 'Woodland Mansion', spacing: 80, separation: 20, salt: 10387319, reach: 2, generate: mansion,
};
